import React from "react";

interface KeyTerm {
  term: string;
  definition: string;
  icon: string;
}

interface Example {
  title: string;
  points: string[];
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  leftSectionTitle?: string;
  rightSectionTitle?: string;
  keyTerms?: string | KeyTerm[];
  examples?: string | Example[];
  bgColor?: string;
}

const SupportingSlide = {
  id: "supporting-slide",
  name: "Supporting Elements Slide",
  fields: [
    {
      id: "title",
      label: "Support Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "",
    },
    {
      id: "leftSectionTitle",
      label: "Left Section Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "rightSectionTitle",
      label: "Right Section Title",
      type: "text",
      defaultValue: "",
    },
    {
      id: "keyTerms",
      label: "Key Terms (JSON array of {term, definition, icon})",
      type: "text",
      defaultValue: "[]",
    },
    {
      id: "examples",
      label: "Examples (JSON array of {title, points[], icon})",
      type: "text",
      defaultValue: "[]",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #064e3b 0%, #065f46 100%)",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      leftSectionTitle = "",
      rightSectionTitle = "",
      bgColor = "linear-gradient(135deg, #064e3b 0%, #065f46 100%)",
      keyTerms = [],
      examples = [],
    } = content;

    let parsedTerms: KeyTerm[] = [];
    let parsedExamples: Example[] = [];

    try {
      if (Array.isArray(keyTerms)) {
        parsedTerms = keyTerms;
      } else if (typeof keyTerms === "string") {
        const parsed = JSON.parse(keyTerms);
        if (Array.isArray(parsed)) {
          parsedTerms = parsed;
        }
      }

      if (Array.isArray(examples)) {
        parsedExamples = examples;
      } else if (typeof examples === "string") {
        const parsed = JSON.parse(examples);
        if (Array.isArray(parsed)) {
          parsedExamples = parsed;
        }
      }
    } catch (error) {
      console.warn("Error parsing content:", error);
    }

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-10">
            <div className="grid grid-cols-12 grid-rows-12">
              {[...Array(144)].map((_, i) => (
                <div
                  key={i}
                  className="w-full h-full border-r border-t border-white/5"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Container */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header Section */}
          <div className="text-center mb-4">
            <h1 className="text-3xl font-black mb-2 relative">
              <span className="relative text-emerald-200">{title}</span>
            </h1>
            <p className="text-base text-emerald-100/90">{subtitle}</p>
            <div className="w-24 h-0.5 mx-auto mt-2 bg-gradient-to-r from-transparent via-emerald-300/30 to-transparent" />
          </div>

          {/* Content Grid */}
          <div className="flex-1 grid grid-cols-2 gap-4 mb-4">
            {/* Left Section */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">📚</span>
                <h2 className="text-lg font-bold text-emerald-100">
                  {leftSectionTitle}
                </h2>
              </div>
              <div className="space-y-3">
                {parsedTerms.map((term, idx) => (
                  <div
                    key={idx}
                    className="group relative bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10
                              transform transition-all duration-300 hover:bg-white/10"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{term.icon}</span>
                      <h3 className="text-base font-bold text-emerald-100">
                        {term.term}
                      </h3>
                    </div>
                    <p className="text-sm text-emerald-100/70 ml-7">
                      {term.definition}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Section */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">🔍</span>
                <h2 className="text-lg font-bold text-emerald-100">
                  {rightSectionTitle}
                </h2>
              </div>
              <div className="space-y-4">
                {parsedExamples.map((example, idx) => (
                  <div
                    key={idx}
                    className="group relative bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10
                              transform transition-all duration-300 hover:bg-white/10"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{example.icon}</span>
                      <h3 className="text-base font-bold text-emerald-100">
                        {example.title}
                      </h3>
                    </div>
                    <ul className="space-y-1.5 ml-7">
                      {example.points.map((point, pointIdx) => (
                        <li
                          key={pointIdx}
                          className="text-sm text-emerald-100/70 flex items-start gap-2"
                        >
                          <span className="text-emerald-300">•</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bottom Decoration */}
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60" />
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/40" />
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/20" />
          </div>
        </div>
      </div>
    );
  },
};

export default SupportingSlide;
